import { useCallback, useMemo, useState } from 'react';
import {
  Alert, Button, Dropdown, Form, Input, message, Modal, Space, Tag, Typography
} from 'antd';
import { MoreOutlined, ThunderboltOutlined } from '@ant-design/icons';
import {
  ErpDataGrid,
  ErpMasterShell,
  useErpMasterQuery
} from '../../../components/common/erp-grid';
import { accountsApi } from './accountsApi';
import './accounts.css';

const { Title } = Typography;

// Stage 6B.2 — Accounting Periods page (enhanced from 6B.1 read-only grid).
//
// New behavior:
//   • "Generate FY Periods" button at the top (monthly + quarterly options).
//   • Status action column with Lock / Unlock / Close (uses the
//     transition table enforced by the backend: open → locked, locked →
//     open / closed; closed is terminal until future Approval MFA).
//   • Row double-click still opens the read-only drawer (grid default).
//
// Inline status edit is intentionally NOT enabled — status transitions
// flow through the action menu so we can hook Approval MFA in a later
// stage at one obvious call site.

const ADAPTER = { list: (q) => accountsApi.listPeriods(q) };

function defaultOnError(err) {
  const msg = err?.response?.data?.error?.message ||
    err?.message || 'Failed to load accounting periods.';
  message.error({ content: msg, key: 'period-list' });
}

const PERIOD_TYPE_COLOR = { month: 'blue', quarter: 'cyan', year: 'gold' };
const STATUS_COLOR      = { open: 'green', locked: 'orange', closed: 'red' };

function fmtDate(v) {
  return v ? String(v).slice(0, 10) : <span className="erp-grid__muted">—</span>;
}

function buildStatusActions(row, onAction) {
  // Pure mapping — keeps the menu consistent with backend transitions:
  //   open    → locked
  //   locked  → open  | closed
  //   closed  → (none)
  const items = [];
  if (row.status === 'open') {
    items.push({ key: 'lock',   label: 'Lock period' });
  }
  if (row.status === 'locked') {
    items.push({ key: 'unlock', label: 'Unlock period' });
    items.push({ key: 'close',  label: 'Close period' });
  }
  if (!items.length) {
    items.push({ key: 'noop', label: '(no transitions allowed)', disabled: true });
  }
  return {
    menu: {
      items,
      onClick: ({ key }) => onAction(row, key)
    }
  };
}

function GeneratePeriodsModal({ open, onClose, onGenerated }) {
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const onSubmit = useCallback(async (values) => {
    setBusy(true);
    try {
      const payload = { financialYear: (values.financialYear || '').trim() };
      const data = values.periodType === 'quarter'
        ? await accountsApi.generateQuarters(payload)
        : await accountsApi.generatePeriods(payload);
      message.success(`Generated ${data.createdCount} (skipped ${data.skippedCount}) for FY ${data.financialYear}.`);
      onGenerated && onGenerated();
      onClose();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to generate periods.');
    } finally { setBusy(false); }
  }, [onGenerated, onClose]);

  return (
    <Modal
      title="Generate Periods" open={open} onCancel={onClose}
      footer={null} destroyOnClose width={480} centered
      className="erp-accounts-modal"
    >
      <Form
        className="erp-accounts-form"
        form={form} layout="vertical" onFinish={onSubmit}
        initialValues={{ financialYear: '2026-27', periodType: 'month' }}
      >
        <Form.Item label="Financial Year (YYYY-YY)" name="financialYear"
          rules={[
            { required: true, message: 'financialYear is required' },
            { pattern: /^[0-9]{4}-[0-9]{2}$/, message: 'Format must be YYYY-YY, e.g. 2026-27' }
          ]}>
          <Input maxLength={9} />
        </Form.Item>
        <Form.Item label="Period Type" name="periodType">
          <Input.Group compact>
            <Button onClick={() => form.setFieldValue('periodType', 'month')}>Monthly</Button>
            <Button onClick={() => form.setFieldValue('periodType', 'quarter')}>Quarterly</Button>
          </Input.Group>
        </Form.Item>
        <Alert type="info" showIcon
          style={{ marginBottom: 12 }}
          message="Rerunning the same FY skips existing periods. Locked and closed periods are never overwritten." />
        <Space>
          <Button type="primary" htmlType="submit" loading={busy} icon={<ThunderboltOutlined />}>
            Generate
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </Space>
      </Form>
    </Modal>
  );
}

export default function AccountingPeriodsPage() {
  const { gridHandlers, refetch } = useErpMasterQuery({
    adapter: ADAPTER, pageSize: 25, onError: defaultOnError
  });
  const [genOpen, setGenOpen] = useState(false);

  const onStatusAction = useCallback(async (row, key) => {
    let nextStatus = null;
    let label = '';
    if (key === 'lock')   { nextStatus = 'locked'; label = 'Lock'; }
    if (key === 'unlock') { nextStatus = 'open';   label = 'Unlock'; }
    if (key === 'close')  { nextStatus = 'closed'; label = 'Close'; }
    if (!nextStatus) return;
    Modal.confirm({
      title: `${label} period "${row.periodName}"?`,
      content: key === 'close'
        ? 'Closed periods are terminal until a future Approval MFA flow is wired. This action cannot be undone via the UI in Stage 6B.2.'
        : `Status will move from "${row.status}" → "${nextStatus}".`,
      okText: label, okButtonProps: { danger: key === 'close' },
      onOk: async () => {
        try {
          await accountsApi.updatePeriod(row.id, { status: nextStatus });
          message.success(`Period "${row.periodName}" → ${nextStatus}.`);
          refetch();
        } catch (e) {
          message.error(e?.response?.data?.error?.message || 'Status transition failed.');
        }
      }
    });
  }, [refetch]);

  const columns = useMemo(() => ([
    { title: 'FY', dataIndex: 'financialYear', key: 'financialYear',
      sortField: 'financial_year', sorter: true,
      width: 110, fixed: 'left', dataType: 'string',
      groupable: true, groupField: 'financial_year',
      render: (v) => <code className="erp-grid__code">{v}</code> },
    { title: 'Period Name', dataIndex: 'periodName', key: 'periodName',
      sortField: 'period_name', sorter: true,
      width: 180, dataType: 'string' },
    { title: 'Type', dataIndex: 'periodType', key: 'periodType',
      sortField: 'period_type', sorter: true,
      width: 90, dataType: 'enum',
      filters: [
        { text: 'Month',   value: 'month' },
        { text: 'Quarter', value: 'quarter' },
        { text: 'Year',    value: 'year' }
      ],
      filterField: 'period_type',
      render: (v) => v
        ? <Tag color={PERIOD_TYPE_COLOR[v] || 'default'} style={{ marginInlineEnd: 0 }}>{v}</Tag>
        : <span className="erp-grid__muted">—</span> },
    { title: 'Start', dataIndex: 'periodStart', key: 'periodStart',
      sortField: 'period_start', sorter: true,
      width: 110, dataType: 'date', render: fmtDate },
    { title: 'End', dataIndex: 'periodEnd', key: 'periodEnd',
      sortField: 'period_end', width: 110, dataType: 'date', render: fmtDate },
    { title: 'GSTR Period', dataIndex: 'gstReturnPeriod', key: 'gstReturnPeriod',
      width: 110, dataType: 'string',
      render: (v) => v ? <code className="erp-grid__code">{v}</code>
                       : <span className="erp-grid__muted">—</span> },
    { title: 'Status', dataIndex: 'status', key: 'status',
      sortField: 'status', sorter: true,
      width: 100, align: 'center', dataType: 'enum',
      groupable: true, groupField: 'status',
      filters: [
        { text: 'Open',   value: 'open' },
        { text: 'Locked', value: 'locked' },
        { text: 'Closed', value: 'closed' }
      ],
      filterField: 'status',
      render: (v) => (
        <Tag color={STATUS_COLOR[v] || 'default'} style={{ marginInlineEnd: 0 }}>
          {(v || '').toUpperCase()}
        </Tag>
      ) },
    { title: '', key: '__actions__',
      width: 56, align: 'center', fixed: 'right',
      render: (_v, row) => (
        <Dropdown trigger={['click']} {...buildStatusActions(row, onStatusAction)}>
          <Button type="text" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
        </Dropdown>
      )
    }
  ]), [onStatusAction]);

  return (
    <ErpMasterShell>
      <div className="erp-accounts-page">
      <Title level={4} style={{ marginTop: 0, marginBottom: 8 }}>Accounting Periods</Title>
      <Alert type="info" banner showIcon style={{ marginBottom: 10 }}
        message="Fiscal calendar for the tenant. Status 'locked' blocks voucher posting; 'closed' is terminal until Approval MFA wiring." />
      <Space style={{ marginBottom: 10 }}>
        <Button type="primary" size="small" icon={<ThunderboltOutlined />} onClick={() => setGenOpen(true)}>
          Generate FY Periods
        </Button>
      </Space>
      <ErpDataGrid
        title="Accounting Periods"
        gridId="accounts-periods-v2"
        columns={columns}
        rowKey="id"
        rowHeight="compact"
        stickyHeader
        searchable
        searchPlaceholder="Search by FY / period name…"
        searchShortcut
        emptyTitle="No accounting periods yet"
        emptyText="Use 'Generate FY Periods' above to create the current FY's 12 monthly periods."
        {...gridHandlers}
      />
      <GeneratePeriodsModal
        open={genOpen} onClose={() => setGenOpen(false)} onGenerated={refetch}
      />
      </div>
    </ErpMasterShell>
  );
}
